import { memo, useState } from 'react';
import { Collapse, IconButton } from '@mui/material';
import { Content, Header, Title, Wrapper } from './ChildModulesWrapper.styles';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ModuleWidget } from './index';
import { mapAgendasToCardItems } from './moduleWidgetUtils';
import type { Module } from 'types/modules';
import { isNumber } from 'radash';

interface ChildModulesWrapperProps {
  childModules: Omit<Module, 'childs'>[];
  onShowAllClick?: (moduleId: string, parentModuleId?: string) => void;
  onDetailClick?: (moduleId: string, agendaSourceType: string, agendaId: string, parentModuleId?: string) => void;
  onAddClick?: (moduleId: string, agendaSourceType: string, agendaId: string, parentModuleId?: string) => void;
  onFavoriteClick?: (moduleId: string, isFavorite: boolean) => void;
  showFavorite?: boolean;
  mock?: boolean;
  parentModuleId?: string;
  isPlainIcon?: boolean;
  maxSubordinates?: number;
}

export const ChildModulesWrapper = memo(
  ({
    childModules,
    onShowAllClick,
    onDetailClick,
    onAddClick,
    onFavoriteClick,
    showFavorite,
    mock,
    parentModuleId,
    isPlainIcon,
    maxSubordinates,
  }: ChildModulesWrapperProps) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    const handleToggle = () => {
      setExpanded(!expanded);
    };

    const visibleChildModules = isNumber(maxSubordinates) ? childModules.slice(0, maxSubordinates) : childModules;

    const childModuleItems = visibleChildModules.map((module) => ({
      title: module.name,
      moduleId: module.moduleId,
      agendas: mapAgendasToCardItems(module.agendas, { maxItems: maxSubordinates }),
      icon: module.icon,
      isPinned: module.isPinned,
      unreadNotificationsCount: module.unreadNotificationsCount,
    }));

    return (
      <Wrapper>
        <Header onClick={handleToggle}>
          <Title>
            {t('user.modules.childModules')} ({visibleChildModules.length})
          </Title>
          <IconButton size="small">{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </Header>
        <Collapse in={expanded} timeout="auto">
          <Content>
            {childModuleItems.map((childModule) => (
              <ModuleWidget
                key={childModule.moduleId}
                title={childModule.title}
                moduleId={childModule.moduleId}
                items={childModule.agendas}
                onShowAllClick={onShowAllClick}
                onDetailClick={onDetailClick}
                onAddClick={onAddClick}
                onFavoriteClick={onFavoriteClick}
                showFavorite={showFavorite}
                isFavorite={childModule.isPinned}
                icon={childModule.icon}
                mock={mock}
                parentModuleId={parentModuleId}
                unreadNotificationsCount={childModule.unreadNotificationsCount}
                isPlainIcon={isPlainIcon}
                maxSubordinates={maxSubordinates}
              />
            ))}
          </Content>
        </Collapse>
      </Wrapper>
    );
  }
);
